import React, { useCallback, useEffect } from 'react';
import { includes, isEmpty, isEqual, without } from 'lodash';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import { arrayOf, bool, shape, string } from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { EMPTY_ARRAY, FILTER_TITLE, STATE_TYPE } from './TypeFilterHelper';
import { filterPropDefaults, filterPropTypes } from '../../page-framework/filter/FilterPropTypes';
import { isShowAllNoneButton } from '../../page-framework/filter/Filter';

const FIVE_FR_FORM_GROUP_MIN = 20;

const useStyles = makeStyles({
    oneFRFormGroup: {
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: '1px',
    },
    twoFRFormGroup: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1px',
    },
    fiveFRFormGroup: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '1px',
    },
    labelItem: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        width: '100%',
    },
    formControlRoot: {
        minWidth: 0,
    },
});

const getFormGroupClassName = (isAllowSelectionEmpty, showAllNoneButton, options, classes) => {
    if (isAllowSelectionEmpty && showAllNoneButton) {
        if (options.length > FIVE_FR_FORM_GROUP_MIN) {
            return classes.fiveFRFormGroup;
        }

        return classes.twoFRFormGroup;
    }

    return classes.oneFRFormGroup;
};

const BaseTypeFilter = ({
    // eslint-disable-next-line no-unused-vars
    filterId,
    options,
    state = EMPTY_ARRAY,
    filterActions,
    isAllowSelectionEmpty = false,
}) => {
    const classes = useStyles();
    const showAllNoneButton = isShowAllNoneButton(options);

    useEffect(() => {
        if (isEmpty(state) && isAllowSelectionEmpty) {
            filterActions.setValid(true);
        } else {
            filterActions.setValid(state.length > 0);
        }
    }, [state, isAllowSelectionEmpty, filterActions]);

    const handleTypeChanged = useCallback(
        (event, type) => {
            const newState = event.target.checked ? [...state, type.value] : without(state, type.value);

            filterActions.setState(newState);
        },
        [state, filterActions]
    );
    const formGroupClassName = getFormGroupClassName(isAllowSelectionEmpty, showAllNoneButton, options, classes);

    return (
        <FormGroup row={false} className={formGroupClassName}>
            {options.map((type) => (
                <Tooltip key={type.value} title={type.name} placement="top" arrow>
                    <FormControlLabel
                        className={classes.formControlRoot}
                        classes={{ label: classes.labelItem }}
                        control={
                            <Checkbox
                                checked={includes(state, type.value)}
                                onChange={(event) => handleTypeChanged(event, type)}
                            />
                        }
                        label={type.name}
                    />
                </Tooltip>
            ))}
        </FormGroup>
    );
};

BaseTypeFilter.propTypes = {
    filterActions: filterPropTypes.filterActions,
    state: STATE_TYPE,
    filterId: string,
    isAllowSelectionEmpty: bool,
    options: arrayOf(
        shape({
            name: string,
            value: string,
        })
    ),
};

BaseTypeFilter.defaultProps = {
    ...filterPropDefaults,
    title: FILTER_TITLE,
    state: EMPTY_ARRAY,
    isAllowSelectionEmpty: false,
};

export default React.memo(
    BaseTypeFilter,
    (oldProps, newProps) =>
        isEqual(oldProps.filterId, newProps.filterId) &&
        isEqual(oldProps.state, newProps.state) &&
        isEqual(oldProps.isAllowSelectionEmpty, newProps.isAllowSelectionEmpty)
);
